<script lang="ts">
  import { store } from '../lib/store.svelte.ts';
  import { router } from '../lib/router.svelte.ts';
  import { daysShort, roundBadge } from '../lib/format.ts';
  import type { Site } from '@chariot/contract';
  import NewCartsSheet from '../lib/NewCartsSheet.svelte';

  /**
   * One site, and what is standing in it right now.
   *
   * The answer comes from the same projection as everything else, so this page cannot
   * disagree with the sweep the operator just finished — it is the same log, read by site
   * instead of by cart.
   */

  let { code }: { code: string } = $props();

  let q = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);
  let renaming = $state(false);
  let newName = $state('');
  let confirmArchive = $state(false);
  let addingCarts = $state(false);

  const site = $derived(store.siteByCode.get(code) ?? null);
  const present = $derived(store.cartsAt(code));
  const rounds = $derived(store.roundsAtSite(code));
  const lastRound = $derived(rounds.find((r) => r.status === 'closed') ?? rounds[0] ?? null);
  const missing = $derived(present.filter((s) => s.missing_since));
  const stale = $derived(present.filter((s) => s.stale && !s.missing_since));

  const rows = $derived.by(() => {
    const needle = q.trim().toLowerCase();
    return present
      .filter((s) => !needle || s.code.toLowerCase().includes(needle) || s.name.toLowerCase().includes(needle))
      // What needs attention first: reported missing, then not confirmed for a long time,
      // then the longest-standing carts.
      .sort((a, b) => {
        const rank = (x: typeof a) => (x.missing_since ? 0 : x.stale ? 1 : 2);
        return rank(a) - rank(b) || (b.days_present ?? 0) - (a.days_present ?? 0) || a.code.localeCompare(b.code);
      });
  });

  const activeElsewhere = $derived(
    store.activeRound && store.activeRound.site_code !== code ? store.activeRound : null,
  );

  async function startHere() {
    await store.openRound(code);
    router.go('');
  }

  async function rename() {
    busy = true;
    error = null;
    try {
      await store.renameSite(code, newName.trim());
      renaming = false;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }

  async function archive(s: Site) {
    busy = true;
    error = null;
    try {
      await store.setSiteStatus(s.code, s.status === 'active' ? 'archived' : 'active');
      confirmArchive = false;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }
</script>

<button class="ghost slim no-print" style="margin-left:-.7rem" onclick={() => router.go('sites')}>
  ← Sites
</button>

{#if !site}
  <p class="banner warn">Site {code} inconnu sur cet appareil. Synchronisez pour le récupérer.</p>
{:else}
  <div class="row" style="margin-top:.4rem;margin-bottom:.1rem">
    <h1 class="grow truncate">{site.name}</h1>
    {#if site.status !== 'active'}<span class="pill warn">archivé</span>{/if}
  </div>
  <p class="mono small subtle" style="margin-bottom:1rem">{site.code}</p>

  <div class="stat-row">
    <div class="stat"><div class="k">présents</div><div class="n">{present.length}</div></div>
    <div class="stat" class:is-danger={missing.length > 0}>
      <div class="k">manquants</div><div class="n">{missing.length}</div>
    </div>
    <div class="stat" class:is-warn={stale.length > 0}>
      <div class="k">non vus &gt; {store.staleAfterDays} j</div><div class="n">{stale.length}</div>
    </div>
    <div class="stat">
      <div class="k">dernier relevé</div>
      <div class="n" style="font-size:1rem">
        {lastRound ? new Date(lastRound.opened_at).toLocaleDateString('fr-FR') : '—'}
      </div>
      {#if lastRound}<div class="sub">{lastRound.scanned.length} scannés</div>{/if}
    </div>
  </div>

  {#if store.activeRound?.site_code === code}
    <button class="primary block no-print" onclick={() => router.go('')}>Reprendre la tournée ici</button>
  {:else if site.status === 'active' && !activeElsewhere}
    <button class="primary block no-print" onclick={startHere}>Démarrer une tournée ici</button>
  {:else if activeElsewhere}
    <div class="banner info">
      Tournée en cours sur {store.siteName(activeElsewhere.site_code)}.
    </div>
  {/if}

  <div class="card">
    <div class="row" style="margin-bottom:.3rem">
      <h2 class="grow" style="margin:0">Chariots ici ({present.length})</h2>
      {#if store.isAdmin}
        <button class="slim no-print" onclick={() => (addingCarts = true)}>Ajouter</button>
      {/if}
    </div>
    {#if present.length > 8}
      <input placeholder="Rechercher…" bind:value={q} aria-label="Rechercher un chariot" />
    {/if}
    {#if rows.length === 0}
      <p class="empty">{present.length === 0 ? 'Aucun chariot enregistré ici.' : 'Aucun résultat.'}</p>
    {:else}
      <ul class="list">
        {#each rows as s (s.code)}
          <li>
            <button class="row-link" onclick={() => router.go(`carts/${s.code}`)}>
              <span class="stack" style="gap:.1rem;align-items:flex-start">
                <span class="truncate row-title">{s.name}</span>
                <span class="mono tiny muted">{s.code}</span>
              </span>
            </button>
            <span class="tiny subtle right">{daysShort(s.days_present)}</span>
            {#if s.missing_since}
              <span class="pill danger">manquant</span>
            {:else if s.stale}
              <span class="pill warn">ancien</span>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  <div class="card">
    <h2>Tournées ici ({rounds.length})</h2>
    {#if rounds.length === 0}
      <p class="empty">Aucune tournée sur ce site.</p>
    {:else}
      <ul class="list">
        {#each rounds.slice(0, 10) as r (r.round_id)}
          {@const b = roundBadge(r)}
          <li>
            <button class="row-link" onclick={() => router.go(`rounds/${r.round_id}`)}>
              <span class="stack" style="gap:.1rem;align-items:flex-start">
                <span class="row-title">{new Date(r.opened_at).toLocaleString('fr-FR')}</span>
                <span class="tiny muted">{roundBadge(r).count}</span>
              </span>
            </button>
            <span class="pill {b.tone}">{b.status}</span>
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  {#if store.isAdmin}
    <div class="card no-print">
      <h2>Administration</h2>
      {#if error}<div class="banner danger">{error}</div>{/if}
      <div class="row wrap">
        <button class="slim" onclick={() => { newName = site.name; renaming = true; }}>Renommer</button>
        <button class="slim" onclick={() => (confirmArchive = true)}>
          {site.status === 'active' ? 'Archiver' : 'Réactiver'}
        </button>
      </div>
    </div>
  {/if}
{/if}

{#if addingCarts && site}
  <NewCartsSheet site_code={site.code} onclose={() => (addingCarts = false)} />
{/if}

{#if renaming && site}
  <div class="overlay" role="dialog" aria-modal="true" aria-label="Renommer le site">
    <div class="sheet">
      <h2>Renommer</h2>
      <label class="field"><span>Nom</span><input bind:value={newName} /></label>
      <div class="row">
        <button class="primary grow" onclick={rename} disabled={busy || !newName.trim()}>Enregistrer</button>
        <button class="ghost" onclick={() => (renaming = false)}>Annuler</button>
      </div>
    </div>
  </div>
{/if}

{#if confirmArchive && site}
  <div class="overlay" role="dialog" aria-modal="true" aria-label="Confirmer">
    <div class="sheet">
      <h2>{site.status === 'active' ? 'Archiver' : 'Réactiver'} {site.name} ?</h2>
      <p class="small muted">
        {#if site.status === 'active'}
          Le site n'apparaîtra plus au démarrage d'une tournée. L'historique et les chariots
          qui y sont enregistrés sont conservés.
        {:else}
          Le site redeviendra sélectionnable pour une tournée.
        {/if}
      </p>
      <div class="row">
        <button class="danger grow" disabled={busy} onclick={() => archive(site)}>Confirmer</button>
        <button class="ghost" onclick={() => (confirmArchive = false)}>Annuler</button>
      </div>
    </div>
  </div>
{/if}
